import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  PlannedSlot,
  formatDayLabel,
  getScheduleDate,
  getScheduleLengthDays,
  getSlotBlockReason,
} from '~/pages/Marketing/AppOnlyFlash/appOnlyFlashCalendar';

import { APP_EXCLUSIVE_BRAND, APP_EXCLUSIVE_SCHEDULE_TYPE } from '~/consts/appExclusive';

import OffersService, { ScheduleRecord } from '~/services/OffersService';

import { appExclusiveScheduleQueryKeys } from './useGetAppExclusiveSchedules';
import { fetchAppExclusiveSlotCheck } from './useGetAppExclusiveSlotCheck';
import { offerQueryKeys } from './useGetOffer';

interface Slot {
  start: string;
  end: string;
}

export interface MoveAppExclusiveScheduleInput {
  schedule: ScheduleRecord;
  /** Offer name for the eligible lookup's name search; null when the offer isn't loaded. */
  searchName: string | null;
  /** Sydney calendar day to move to, YYYY-MM-DD. */
  targetDate: string;
  /** Deal on the full target day picked to swap; it takes the moving deal's old day. */
  swapWith?: { schedule: ScheduleRecord; offerName: string; searchName: string | null; targetDate: string };
}

/**
 * Re-runs the eligible lookup for `schedule`'s offer on `date` right before writing and returns the fresh
 * slot, keeping the row's length (24 or 48 hours). The dialog's check can be minutes old, and svc-offer
 * only guards the daily limit on write, not the on-sale window.
 */
async function checkSlot(
  schedule: ScheduleRecord,
  date: string,
  searchName: string | null,
  ignoreScheduleIds: Array<number>,
  plannedSlots: Array<PlannedSlot> = [],
): Promise<Slot> {
  const offerId = schedule.fk_offer_salesforce_id;
  const eligible = await fetchAppExclusiveSlotCheck({
    region: schedule.region,
    date,
    days: getScheduleLengthDays(schedule),
    offerId,
    offerName: searchName,
  });
  const reason = getSlotBlockReason(eligible, offerId, ignoreScheduleIds, undefined, plannedSlots);
  if (reason) {
    throw new Error(`${formatDayLabel(date)} no longer fits: ${reason}`);
  }
  return { start: eligible.slot.start, end: eligible.slot.end };
}

function createAppExclusive(offerId: string, region: string, { start, end }: Slot) {
  return OffersService.createSchedule(
    { type: APP_EXCLUSIVE_SCHEDULE_TYPE, brand: APP_EXCLUSIVE_BRAND, region, start, end },
    offerId,
  );
}

/**
 * Moves an App Only Flash row to another day, optionally swapping with a row on that day when it is full.
 * svc-offer rejects a day over its deal limit and has no batch write, so a swap removes the other row,
 * moves this one, then re-adds the other on the freed day. A failed move puts the other
 * row back where it was; if that also fails, the error names the removed deal so it can be re-added.
 * These three writes aren't atomic: a single swap endpoint in svc-offer would remove the gaps.
 */
async function moveAppExclusive({ schedule, searchName, targetDate, swapWith }: MoveAppExclusiveScheduleInput) {
  const offerId = schedule.fk_offer_salesforce_id;
  // Both rows leave their old days, so neither counts there. Checked before any write.
  const ignore = swapWith ? [schedule.id, swapWith.schedule.id] : [schedule.id];
  const target = await checkSlot(schedule, targetDate, searchName, ignore);
  // The other row is re-added last, after this one has moved. With 48 hour slots the two new slots can
  // share a day, so this row's new slot counts there; otherwise the re-add could fail after the delete.
  // Runs after the first check because it needs that slot.
  const swapTarget = swapWith
    ? await checkSlot(swapWith.schedule, swapWith.targetDate, swapWith.searchName, ignore, [target])
    : null;

  if (!swapWith) {
    await OffersService.updateSchedule(target, offerId, schedule.id);
    return;
  }

  const other = swapWith.schedule;
  await OffersService.deleteSchedule(other.fk_offer_salesforce_id, other.id);
  try {
    await OffersService.updateSchedule(target, offerId, schedule.id);
  } catch (moveErr) {
    try {
      await createAppExclusive(other.fk_offer_salesforce_id, other.region, other);
    } catch (restoreErr) {
      // The other row is already deleted: the admin must know it's gone, or its day stays empty.
      throw new Error(
        `${moveErr?.message ?? 'unknown error'}. ${swapWith.offerName} was removed for the swap and could not be put back on ${formatDayLabel(getScheduleDate(other))} (${restoreErr?.message ?? 'unknown error'}). Add it again from the calendar.`,
        { cause: restoreErr },
      );
    }
    throw moveErr;
  }
  try {
    await createAppExclusive(other.fk_offer_salesforce_id, other.region, swapTarget);
  } catch (err) {
    throw new Error(
      `the deal moved, but ${swapWith.offerName} could not be re-added (${err?.message ?? 'unknown error'}). Add it again from the calendar.`,
      { cause: err },
    );
  }
}

export function useMoveAppExclusiveSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: moveAppExclusive,
    // Invalidate on failure too: a partial swap still changes the calendar.
    onSettled: (_data, _error, { schedule, swapWith }) => {
      queryClient.invalidateQueries({ queryKey: appExclusiveScheduleQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.detail(schedule.fk_offer_salesforce_id) });
      if (swapWith) {
        queryClient.invalidateQueries({
          queryKey: offerQueryKeys.detail(swapWith.schedule.fk_offer_salesforce_id),
        });
      }
    },
  });
}
